import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { fetchTaskByIdAsync, selectTask, clearSelectedTask } from '../slice/taskSlice'
import { useThemeColor } from '../hooks/useThemeColor'
import { useTranslation } from '../i18n/LanguageContext'
import { X, ChevronRight } from 'lucide-react'

import OverViewComp from './settings/components/Overview'
import StandarQustions from './settings/components/StandarQustions'
import SystemPrompts from './settings/components/SystemPrompts'
import AgentTools from './settings/components/AgentTools'
import DefaultFiles from './settings/components/DefaultFiles'
import TaskShortcuts from './settings/components/TaskShortcuts'
import ProjectOverviewBuilder from './settings/components/ProjectOverviewBuilder'
import ProjectSetupBuilder from './settings/components/ProjectSetupBuilder'

const AgentSettings = () => {
  const { agentId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { aispaceColor } = useThemeColor()
  const { t } = useTranslation()

  const [selectedTab, setSelectedTab] = useState('general')
  const [isLoading, setIsLoading] = useState(true)

  const { currentTask } = useSelector((state) => state.tasks)
  const selectedTask = useSelector((state) => state.tasks.selectedTask)

  // Fetch agent data on mount
  useEffect(() => {
    if (agentId) {
      setIsLoading(true)
      dispatch(fetchTaskByIdAsync(agentId))
        .unwrap()
        .then((task) => {
          dispatch(selectTask(task))
          setIsLoading(false)
        })
        .catch((error) => {
          console.error('Failed to fetch agent:', error)
          setIsLoading(false)
        })
    }

    return () => {
      dispatch(clearSelectedTask())
    }
  }, [agentId, dispatch])

  const handleClose = () => {
    dispatch(clearSelectedTask())
    navigate('/agents')
  }

  const tabs = [
    { id: 'general', label: t('agentSettings.tabGeneral') },
    { id: 'questions', label: t('agentSettings.tabQuestions') },
    { id: 'ai-profile', label: t('agentSettings.tabAiProfile') },
    { id: 'tools', label: t('agentSettings.tabTools') },
    { id: 'files', label: t('agentSettings.tabFiles') },
    { id: 'project-overview', label: t('agentSettings.tabProjectOverview') },
    { id: 'project-setup', label: t('agentSettings.tabProjectSetup') },
    { id: 'shortcuts', label: t('agentSettings.tabShortcuts') }
  ]

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'general':
        return <OverViewComp />
      case 'questions':
        return <StandarQustions />
      case 'ai-profile':
        return <SystemPrompts />
      case 'tools':
        return <AgentTools taskId={agentId} />
      case 'files':
        return <DefaultFiles />
      case 'project-overview':
        return <ProjectOverviewBuilder taskId={agentId} />
      case 'project-setup':
        return <ProjectSetupBuilder taskId={agentId} />
      case 'shortcuts':
        return <TaskShortcuts />
      default:
        return <OverViewComp />
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: aispaceColor }}></div>
      </div>
    )
  }

  const agent = selectedTask || currentTask

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 px-6 py-4 text-[14px]">
        <button
          onClick={handleClose}
          className="flex items-center gap-2 text-[#6B7280] hover:text-[#374151] transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 4L4 8L12 12L20 8L12 4Z" />
            <path d="M4 12L12 16L20 12" />
            <path d="M4 16L12 20L20 16" />
          </svg>
          <span>{t('agentSettings.breadcrumbAgents')}</span>
        </button>
        <ChevronRight size={16} className="text-[#9CA3AF]" />
        <span className="text-[#111827] font-medium">{t('agentSettings.breadcrumbSettings')}</span>
      </div>

      {/* Agent Header Card */}
      <div className="mx-6 mb-4 bg-white rounded-xl shadow-sm p-6 border border-[#E5E7EB]">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[24px] font-semibold text-[#111827]">
              {agent?.name || t('agentSettings.agentFallback')}
            </h1>
            <p className="text-[14px] text-[#6B7280] mt-1">
              {agent?.department_name || t('agentSettings.departmentFallback')}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-[#F3F4F6] rounded-lg transition-colors"
          >
            <X size={20} className="text-[#6B7280]" />
          </button>
        </div>
      </div>

      {/* Tabs - Outside the header card */}
      <div className="mx-6 border-b border-[#E5E7EB] mb-6">
        <div className="flex items-center gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              className="pb-3 text-[14px] font-medium transition-colors relative"
              style={{ color: selectedTab === tab.id ? aispaceColor : '#9E9E9E' }}
            >
              {tab.label}
              {selectedTab === tab.id && (
                <div
                  className="absolute bottom-0 left-0 right-0 h-[2px]"
                  style={{ backgroundColor: aispaceColor }}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="mx-6 pb-8 min-w-0">
        {renderTabContent()}
      </div>
    </div>
  )
}

export default AgentSettings
